import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import useFocusTrap from '../hooks/useFocusTrap';

/**
 * OkcHealthIndicator — Header badge for ÖKC (yazarkasa) health.
 *
 * FAZ 2.2 health-monitor event API'sine bağlanır:
 *   window.electronOKC.monitorStatus(deviceId)  → { color, ok, lastCheck, consecutiveFails, lastError }
 *   window.electronOKC.onHealthTick(cb)         → unsubscribe fn (tick: green/yellow/red)
 *   window.electronOKC.onHealthAlarm(cb)        → unsubscribe fn (3+ ardışık fail)
 *   window.electronOKC.preflight(deviceId)      → manuel sağlık kontrolü
 *   window.electronOKC.diagExport()             → tanılama zip
 *
 * Web fallback: window.electronOKC yoksa null render.
 *
 * A11y:
 *   - Trigger button: aria-expanded, aria-haspopup, aria-label (durum dahil)
 *   - Popover: role="dialog" aria-modal="true" aria-labelledby + useFocusTrap (Esc kapatır)
 *   - Click-outside dismiss (mousedown listener)
 *   - Status sr-only live region (renk değişimini ekran okuyucu anonsu)
 */
export default function OkcHealthIndicator({ deviceId }) {
  const { t } = useLanguage();
  const [health, setHealth] = useState({ color: 'gray', ok: false, lastError: null });
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'alarm'|'preflight', message }
  const containerRef = useRef(null);
  const popoverRef = useRef(null);
  const titleId = useRef(`okc-health-title-${Math.random().toString(36).slice(2, 8)}`);

  // Focus trap + Esc close
  useFocusTrap(open, popoverRef, { onEscape: () => setOpen(false) });

  // ── Audio cue (Web Audio, paketsiz — Header.beep ile aynı pattern) ─────────
  const playAlarmCue = useCallback(() => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 660;
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.45);
      osc.start();
      osc.stop(ctx.currentTime + 0.45);
    } catch { /* autoplay engeli — sessiz geç */ }
    try { if (navigator.vibrate) navigator.vibrate([120, 60, 120]); } catch { /* */ }
  }, []);

  // ── Subscribe to monitor ─────────────────────────────────────────────────
  useEffect(() => {
    if (!window.electronOKC || !deviceId) return undefined;

    let cancelled = false;

    // Initial fetch (tek seferlik snapshot)
    if (typeof window.electronOKC.monitorStatus === 'function') {
      window.electronOKC.monitorStatus(deviceId)
        .then((data) => { if (!cancelled && data) setHealth((prev) => ({ ...prev, ...data })); })
        .catch(() => { /* monitor henüz başlamamış olabilir */ });
    }

    // Health-monitor henüz başlamadıysa başlat (idempotent)
    if (typeof window.electronOKC.monitorStart === 'function') {
      window.electronOKC.monitorStart(deviceId).catch(() => {});
    }

    const unsubscribeTick = window.electronOKC.onHealthTick?.((data) => {
      if (cancelled || !data || data.deviceId !== deviceId) return;
      setHealth((prev) => ({
        ...prev,
        color: data.color,
        ok: data.ok,
        lastCheck: data.timestamp,
        consecutiveFails: data.consecutiveFails,
        warning: data.warning,
        lastError: data.error || null,
        latencyMs: data.latencyMs,
      }));
    });

    const unsubscribeAlarm = window.electronOKC.onHealthAlarm?.((data) => {
      if (cancelled || !data || data.deviceId !== deviceId) return;
      playAlarmCue();
      setToast({
        type: 'alarm',
        message: t('okcAlarmFired', 'Yazarkasa bağlantısı kesildi — kontrol edin.'),
      });
      // Auto-dismiss toast after 6s
      setTimeout(() => setToast((cur) => (cur && cur.type === 'alarm' ? null : cur)), 6000);
    });

    return () => {
      cancelled = true;
      try { unsubscribeTick?.(); } catch { /* */ }
      try { unsubscribeAlarm?.(); } catch { /* */ }
    };
  }, [deviceId, t, playAlarmCue]);

  // ── Click-outside dismiss ────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // ── Web fallback: Electron yoksa hiç render etme ─────────────────────────
  if (typeof window === 'undefined' || !window.electronOKC) return null;
  if (!deviceId) return null;

  const colorMap = {
    green: 'bg-emerald-500',
    yellow: 'bg-amber-500 animate-pulse',
    red: 'bg-red-500 animate-pulse',
    gray: 'bg-gray-400',
  };

  const statusLabel = {
    green: t('okcOnline', 'Çevrimiçi'),
    yellow: t('okcWarning', 'Uyarı'),
    red: t('okcOffline', 'Çevrimdışı'),
    gray: t('okcUnknown', 'Bilinmiyor'),
  }[health.color || 'gray'];

  const runPreflight = async () => {
    if (typeof window.electronOKC.preflight !== 'function') return;
    setToast({ type: 'preflight', message: t('okcPreflightRunning', 'Sağlık kontrolü çalışıyor…') });
    try {
      const r = await window.electronOKC.preflight(deviceId);
      setToast({
        type: 'preflight',
        message: r && r.ok
          ? t('okcPreflightOk', 'Sağlık kontrolü başarılı.')
          : (r && r.error) || t('okcPreflightFail', 'Sağlık kontrolü başarısız.'),
      });
    } catch (err) {
      setToast({ type: 'preflight', message: err.message || t('okcPreflightFail', 'Sağlık kontrolü başarısız.') });
    }
    setTimeout(() => setToast((cur) => (cur && cur.type === 'preflight' ? null : cur)), 4000);
  };

  const exportDiag = () => {
    try { window.electronOKC.diagExport?.(); } catch { /* */ }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        aria-label={`${t('okcHealthIndicator', 'Yazarkasa Durumu')}: ${statusLabel}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={`${t('okcHealthIndicator', 'Yazarkasa Durumu')}: ${statusLabel}`}
      >
        <span
          className={`w-2.5 h-2.5 rounded-full ${colorMap[health.color || 'gray']}`}
          aria-hidden="true"
        />
        <span className="text-xs font-medium hidden sm:inline text-gray-700 dark:text-gray-200">
          {t('okcShort', 'ÖKC')}
        </span>
        <span className="sr-only" role="status" aria-live="polite">{statusLabel}</span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId.current}
          className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-50 p-3 text-sm"
        >
          <div id={titleId.current} className="font-semibold text-gray-800 dark:text-gray-100 mb-2 flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${colorMap[health.color || 'gray']}`} aria-hidden="true" />
            <span>{t('okcStatus', 'Durum')}: {statusLabel}</span>
          </div>
          <dl className="space-y-1 text-gray-700 dark:text-gray-200">
            <div className="flex justify-between gap-2">
              <dt className="text-xs text-gray-500 dark:text-gray-400">{t('okcDeviceId', 'Cihaz')}</dt>
              <dd className="text-xs font-mono truncate max-w-[160px]" title={deviceId}>{deviceId}</dd>
            </div>
            {health.lastCheck && (
              <div className="flex justify-between gap-2">
                <dt className="text-xs text-gray-500 dark:text-gray-400">{t('lastCheck', 'Son kontrol')}</dt>
                <dd className="text-xs">{new Date(health.lastCheck).toLocaleTimeString('tr-TR')}</dd>
              </div>
            )}
            {typeof health.latencyMs === 'number' && (
              <div className="flex justify-between gap-2">
                <dt className="text-xs text-gray-500 dark:text-gray-400">{t('okcLatency', 'Gecikme')}</dt>
                <dd className="text-xs">{health.latencyMs} ms</dd>
              </div>
            )}
            {health.consecutiveFails > 0 && (
              <div className="flex justify-between gap-2">
                <dt className="text-xs text-red-500">{t('failsInARow', 'Ardışık hata')}</dt>
                <dd className="text-xs text-red-500 font-semibold">{health.consecutiveFails}</dd>
              </div>
            )}
            {health.lastError && (
              <div className="text-red-600 dark:text-red-400 text-xs mt-1 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1 break-words">
                {(typeof health.lastError === 'object' && (health.lastError.title || health.lastError.message)) || String(health.lastError)}
              </div>
            )}
          </dl>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={runPreflight}
              className="flex-1 text-xs px-2 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 transition-colors"
            >
              {t('runPreflight', 'Sağlık Kontrolü')}
            </button>
            <button
              type="button"
              onClick={exportDiag}
              className="flex-1 text-xs px-2 py-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 transition-colors"
            >
              {t('exportDiag', 'Tanılama')}
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div
          role="status"
          aria-live="assertive"
          className={`fixed bottom-4 right-4 z-[70] max-w-xs px-3 py-2 rounded-lg shadow-lg text-sm text-white ${
            toast.type === 'alarm' ? 'bg-red-600' : 'bg-blue-600'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
